/**
 * Axios HTTP client — dual auth: httpOnly cookie (primary) + Bearer token
 * fallback for mobile.
 *
 * The server sets `auth_token` as an httpOnly cookie on POST /api/auth/login.
 * `withCredentials: true` ensures axios sends the cookie back on every
 * subsequent same-origin request.
 *
 * Mobile browsers (Safari, Chrome Android) frequently drop the httpOnly
 * cookie between sessions — low-memory eviction, aggressive background-tab
 * cleanup, private mode, etc. To survive that, the auth provider also
 * stores the JWT in `localStorage` and this client attaches it as
 * `Authorization: Bearer` on every request. The server's
 * `authJwtVerify()` accepts either source (cookie or Bearer), so if the
 * cookie is gone, Bearer still authenticates the request.
 *
 * For routes that explicitly set their own Authorization header (e.g.
 * AuthProvider's restore-session retry), the existing header is preserved.
 */
import axios, { type AxiosError, type AxiosInstance } from 'axios'
import toast from 'react-hot-toast'
import { config } from './env.js'

const TOKEN_STORAGE_KEY = 'admin_token'

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY) ?? sessionStorage.getItem(TOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

export const httpClient: AxiosInstance = axios.create({
  baseURL: config.API_URL,
  timeout: 30_000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor: attach Authorization: Bearer <token> from storage
// if not already set by the caller. This is what lets the session survive
// on mobile when the browser drops the httpOnly cookie.
httpClient.interceptors.request.use((req) => {
  if (req.headers.Authorization) return req
  const token = readStoredToken()
  if (token) {
    req.headers.Authorization = `Bearer ${token}`
  }
  return req
})

httpClient.interceptors.response.use(
  (r) => r,
  (err: AxiosError<{ message?: string; error?: string }>) => {
    const status = err.response?.status
    const serverMessage = err.response?.data?.message
    if (status === 401) {
      // Don't redirect when we're on the login page itself — the login form
      // owns its own 401 handling (toast "Sai tên đăng nhập hoặc mật khẩu").
      // Redirecting here would replace the page before the catch in
      // LoginPage can show the toast.
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
    } else if (status === 403) {
      toast.error(serverMessage ?? 'Bạn không có quyền thực hiện')
    } else if (status && status >= 400) {
      // Show server message when available — covers 409 duplicate, 400 validation, etc.
      toast.error(serverMessage ?? 'Có lỗi xảy ra, vui lòng thử lại')
    }
    return Promise.reject(err)
  },
)

/** Resolve a live screen event into an <img> src.
 *  Priority:
 *    1. dataBase64 (transient zero-cost realtime) → data: URL, no HTTP.
 *    2. meta.url (persisted live mode) → absolute URL + correct cache-bust
 *       (`&v=` when a `?token=` query already exists — the old call sites
 *       always appended `?v=` which produced `?token=xxx?v=...`).
 *  Returns null when the event carries no renderable frame. */
export function resolveScreenSrc(event: {
  meta: { url: string; capturedAt?: string | null; id: number | string }
  dataBase64?: string | null
}): string | null {
  if (event.dataBase64) return `data:image/jpeg;base64,${event.dataBase64}`
  if (event.meta.url) {
    const base = resolveScreenshotUrl(event.meta.url)
    const sep = base.includes('?') ? '&' : '?'
    const v = event.meta.capturedAt ?? event.meta.id
    return `${base}${sep}v=${encodeURIComponent(String(v))}`
  }
  return null
}

/** Resolve a screenshot URL returned by the server into an absolute URL.
 *  Appends the admin JWT as `?token=` so the BE can serve the file
 *  even when the request comes from a cross-origin <img> tag that
 *  doesn't carry the httpOnly auth cookie. */
export function resolveScreenshotUrl(url: string): string {
  const absolute = url.startsWith('http') ? url : `${config.API_URL}${url}`
  // Read token from localStorage (stored by AuthProvider after login).
  // localStorage survives tab close on mobile browsers — required because
  // httpOnly cookies aren't sent on cross-origin <img> loads.
  const token = readStoredToken()
  if (!token) return absolute
  // URL may already have a query (e.g. cache-buster); use URLSearchParams.
  const sep = absolute.includes('?') ? '&' : '?'
  return `${absolute}${sep}token=${encodeURIComponent(token)}`
}
